import { Component, OnInit, Input } from '@angular/core';
import { ProgressBarMode } from '@angular/material/progress-bar';
import { FieldLargeProgressBar } from '../../models/IFieldLargeProgressBar';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import findLastIndex from "lodash/findLastIndex";

const Small = 'Small';
const LargeHorizontal = 'LargeHorizontal';
const LargeVertical = 'LargeVertical';

@Component({
  selector: 'ff-progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrls: ['./progress-bar.component.scss'],
  providers: [{
    provide: STEPPER_GLOBAL_OPTIONS, useValue: {displayDefaultIndicatorType: false}
  }]
})

/**
 * It is necessary to include a template ref in the parent component for the
 * vertical and horizontal progress bars (<ng-template #germanSheperdCard>).
 * Example of using:
 * <app-progress-bar
 * [value]="90"
 * [size]="'LargeVertical'"
 * [fieldsLargeProgressBar]="this.fieldsLargeProgressBar"
 * [customTemplate]="{ 'shibaCard': shibaCard, 'germanSheperdCard': germanSheperdCard, 'label': label }">
 * </app-progress-bar>
 */
export class ProgressBarComponent implements OnInit {

  /**
   * The value to show in the small progress bar.
   * @internal
   */
  @Input() value = 0;

  /**
   * The mode of the small progress bar.
   * Default value: determinate.
   * @internal
   */
  mode: ProgressBarMode = 'determinate';

  /**
   * Indicates what progress bar or progress indicator to use.
   * Default value: LargeHorizontal.
   * @internal
   */
  @Input() size: string = LargeHorizontal;

  /**
   * Indicates if the large horizontal progress is editable or not.
   * Default value: true.
   * @internal
   */
  isEditable = true;

  /**
   * Is the array of the components we want to use in every step,
   * the emitter emitComponent will send the specific component
   * when we click the step.
   * Important note: The componentTypeDescription must be unique.
   * Example: this.fieldsLargeProgressBar =
   * [{ componentTypeDescription: 'shibaCard', label: 'In the US', onGoingOrCompleted: true, state: 'place', newStateMaterialIcon: 'call_end' },
   *  { componentTypeDescription: 'label', label: 'On going', onGoingOrCompleted: true, state: 'travel', newStateMaterialIcon: 'forum' },
   *  { componentTypeDescription: 'germanSheperdCard', label: 'Finished', onGoingOrCompleted: true, state: 'completed', newStateMaterialIcon: 'announcement' }]
   * @internal
   */
  @Input() fieldsLargeProgressBar: FieldLargeProgressBar[];

  /**
   * Is a variable used to indicates the selected item step position
   * for the large vertical and horizontal component.
   * @internal
   */
  selectedItemPosition = 0;

  /**
   * The corresponding input array, it is necessary to indicate the
   * same component type keys of the fieldsLargeProgressBar array here.
   * Used for the large vertical and horizontal progress bar.
   * Example: { 'shibaCard': shibaCard, 'germanSheperdCard': germanSheperdCard, 'label': label }
   * @internal
   */
  @Input() customTemplate: JSON;

  /**
   * An array that saves the component types.
   * @internal
   */
  componentTypes: string[] = [];

  ngOnInit(): void {
    if (this.size !== Small && this.size !== LargeHorizontal && this.size !== LargeVertical) {
      this.size = LargeHorizontal;
    }
    this.setSelectedItem();
  }

  /**
   * Sets the component types array using the component type.
   */
  setComponentTypes = (): void => {
    if(this.componentTypes.length < this.fieldsLargeProgressBar.length){
      this.fieldsLargeProgressBar.forEach(element => {
        this.componentTypes.push(element.componentTypeDescription);
      });
    }
  }

  /**
   * Sets the custom template based on the element and the component type.
   * @param element The corresponding element of the template.
   * @param componentTypeDescription The corresponding component type.
   * @returns A custom template based on the element.
   */
  setCustomTemplate = (element: string, componentTypeDescription: string): any => {
    if(element === componentTypeDescription){
      return this.customTemplate[element];
    }
  }

  /**
   * Sets the selected item for the large horizontal progress indicator.
   * @returns The current index.
   */
  setSelectedItem = (): void => {
    const lastIndex =
      findLastIndex(this.fieldsLargeProgressBar, item => item.onGoingOrCompleted === true);
    this.selectedItemPosition = lastIndex;
     if(this.selectedItemPosition <= -1){
      this.selectedItemPosition = 0;
     }
  }
}
